import type { ReactNode } from 'react';
import { ApiError } from '@/lib/api-error';
import type { QueryLike } from './query-like';
import { ErrorState, LoadingState, NotFoundState, PermissionDeniedState } from './states';

/**
 * Os estados obrigatórios de toda tela com dados (§29) num lugar só: carregando, erro, sem
 * permissão, não encontrado, vazio e os dados. A tela escreve só o caso feliz e, se quiser, o
 * vazio — ninguém esquece o erro, que é o que deixava a área em branco quando a API falhava.
 */
export function QueryState<T>({
  query,
  children,
  loading,
  empty,
  isEmpty,
  notFound,
  frame = (node) => node,
}: {
  /** Uma consulta do TanStack, ou várias com `combineQueries`. */
  query: QueryLike<T>;
  children: (data: T) => ReactNode;
  /** Esqueleto no formato do conteúdo; o padrão são três linhas. */
  loading?: ReactNode;
  /** O que mostrar quando `isEmpty` diz que não há nada — normalmente um `EmptyState`. */
  empty?: ReactNode;
  isEmpty?: (data: T) => boolean;
  /** Título do 404 ("Produto não encontrado"). Sem ele, 404 é erro comum. */
  notFound?: string;
  /**
   * Moldura dos estados que não são dados (carregando, erro, vazio), para quando o conteúdo traz
   * a própria `<Page>` e o estado ficaria sem ela.
   */
  frame?: (state: ReactNode) => ReactNode;
}) {
  if (query.isError) {
    const status = query.error instanceof ApiError ? query.error.status : null;
    if (status === 403) return <>{frame(<PermissionDeniedState />)}</>;
    // Consulta de detalhe só recebe 400 por identificador malformado no endereço — para quem
    // usa, é o mesmo que não existir.
    if ((status === 404 || status === 400) && notFound)
      return <>{frame(<NotFoundState title={notFound} />)}</>;
    return <>{frame(<ErrorState error={query.error} onRetry={() => void query.refetch()} />)}</>;
  }
  if (query.isPending || query.data === undefined) return <>{frame(loading ?? <LoadingState />)}</>;
  if (empty && isEmpty?.(query.data)) return <>{frame(empty)}</>;
  return <>{children(query.data)}</>;
}
